import request from './request'

// 课程分类
export interface Category {
  id: string
  name: string
  icon?: string
  emoji?: string
  count?: number
}

// 课程章节
export interface Chapter {
  id: string
  title: string
  sort_order: number
  lessons: Lesson[]
}

// 课程课时
export interface Lesson {
  id: string
  title: string
  video_url: string
  duration: string
  sort_order: number
}

// 课程正文
export interface CourseBody {
  description: string
  outline: string
}

// 课程
export interface Course {
  id: string
  title: string
  cover: string
  video_url: string
  price: number
  is_free: boolean
  teacher: string
  duration: string
  category_id: string
  category_name: string
  view_count: number
  like_count: number
  student_count: number
  lesson_count: number
  description?: string
  emoji?: string
  status?: number
  created_at?: string
  updated_at?: string
  rating?: number
  progress?: number
}

// 课程详情
export interface CourseDetail {
  course: Course
  course_body: CourseBody
  chapters: Chapter[]
}

// 课程笔记
export interface CourseNote {
  id: string
  course_id: string
  lesson_id: string
  user_id: string
  username: string
  avatar: string
  content: string
  like_count: number
  is_liked: boolean
  created_at: string
}

// 获取课程列表请求
export interface GetCoursesReq {
  page?: number
  page_size?: number
  category_id?: string
  keyword?: string
  status?: number
}

// 获取课程列表响应
export interface GetCoursesResp {
  list: Course[]
  total: number
}

// 获取课程详情请求
export interface GetCourseDetailReq {
  id: string
}

// 获取课程详情响应
export interface GetCourseDetailResp extends CourseDetail {}

// 创建课程笔记请求
export interface CreateCourseNoteReq {
  course_id: string
  lesson_id: string
  content: string
}

// 创建课程笔记响应
export interface CreateCourseNoteResp {
  id: string
}

// 获取课程笔记列表请求
export interface GetCourseNotesReq {
  course_id: string
  lesson_id?: string
  page?: number
  page_size?: number
}

// 获取课程笔记列表响应
export interface GetCourseNotesResp {
  list: CourseNote[]
  total: number
}

// 更新课程笔记请求
export interface UpdateCourseNoteReq {
  id: string
  content: string
}

// 更新课程笔记响应
export interface UpdateCourseNoteResp {
  success: boolean
}

// 删除课程笔记请求
export interface DeleteCourseNoteReq {
  id: string
}

// 删除课程笔记响应
export interface DeleteCourseNoteResp {
  success: boolean
}

// 点赞课程笔记请求
export interface LikeCourseNoteReq {
  id: string
}

// 点赞课程笔记响应
export interface LikeCourseNoteResp {
  success: boolean
  like_count: number
}

// 获取分类列表响应
export interface GetCategoriesResp {
  list: Category[]
  total: number
  page: number
  page_size: number
}

// 获取分类列表
export function getCategories(params?: { biz_type?: number; keyword?: string; status?: number; page?: number; page_size?: number }): Promise<GetCategoriesResp> {
  return request.get('/categories', { params })
}

// 获取课程列表
export function getCourses(params: GetCoursesReq): Promise<GetCoursesResp> {
  return request.get('/courses', { params })
}

// 获取推荐课程
export function getRecommendedCourses(): Promise<Course[]> {
  return request.get('/courses/recommended')
}

// 获取热门课程
export function getHotCourses(): Promise<Course[]> {
  return request.get('/courses/hot').then(res => res.data || [])
}

// 获取免费课程
export function getFreeCourses(params: { page?: number; page_size?: number }): Promise<GetCoursesResp> {
  return request.get('/courses/free', { params })
}

// 获取课程详情
export function getCourseDetail(id: string): Promise<GetCourseDetailResp> {
  return request.get(`/courses/${id}`)
}

// 增加课程浏览量
export function incrementViewCount(id: string): Promise<{ success: boolean; view_count: number }> {
  return request.post(`/courses/${id}/view`)
}

// 增加课程学习人数
export function incrementStudentCount(id: string): Promise<{ success: boolean; student_count: number }> {
  return request.post(`/courses/${id}/student`)
}

// 创建课程笔记
export function createCourseNote(data: CreateCourseNoteReq): Promise<CreateCourseNoteResp> {
  return request.post('/course-notes', data)
}

// 获取课程笔记列表
export function getCourseNotes(params: GetCourseNotesReq): Promise<GetCourseNotesResp> {
  return request.get('/course-notes', { params })
}

// 获取课程笔记详情
export function getCourseNoteDetail(id: string): Promise<CourseNote> {
  return request.get(`/course-notes/${id}`)
}

// 更新课程笔记
export function updateCourseNote(id: string, data: { content: string }): Promise<UpdateCourseNoteResp> {
  return request.put(`/course-notes/${id}`, data)
}

// 删除课程笔记
export function deleteCourseNote(id: string): Promise<DeleteCourseNoteResp> {
  return request.delete(`/course-notes/${id}`)
}

// 点赞课程笔记
export function likeCourseNote(id: string): Promise<LikeCourseNoteResp> {
  return request.post(`/course-notes/${id}/like`)
}

// ========== Admin API ==========

// 创建课程请求
export interface CreateCourseReq {
  title: string
  description: string
  cover: string
  video_url: string
  price: number
  is_free: boolean
  teacher: string
  duration: string
  category_id: string
  body: {
    description: string
    outline: string
  }
  chapters?: {
    title: string
    lessons: {
      title: string
      video_url: string
      duration: string
    }[]
  }[]
}

// 创建课程响应
export interface CreateCourseResp {
  id: string
}

// 更新课程请求
export interface UpdateCourseReq {
  title?: string
  description?: string
  cover?: string
  video_url?: string
  price?: number
  is_free?: boolean
  teacher?: string
  duration?: string
  category_id?: string
  status?: number
  body?: {
    description: string
    outline: string
  }
}

// 创建课程
export function createCourse(data: CreateCourseReq): Promise<CreateCourseResp> {
  return request.post('/admin/courses', data)
}

// 更新课程
export function updateCourse(id: string, data: UpdateCourseReq): Promise<{ success: boolean }> {
  return request.put(`/admin/courses/${id}`, data)
}

// 删除课程
export function deleteCourse(id: string): Promise<{ success: boolean }> {
  return request.delete(`/admin/courses/${id}`)
}


